<template>
  <div class="breadcrumb-for-item-detail">
    <!-- Coming from "All Tasks/Convos"... -->
    <!-- Task and convo detail route.params passes entity and ref # to prevent flickering -->
    <!-- Coming from a listing page... -->
    <template v-if="fromAll || fromEntityLanding">
      <breadcrumb-listing-picker
        :selected-id="cachedBreadcrumbData.selectedEntityID"
        :title="cachedBreadcrumbData.title"
        :all-tasks-selected="fromAllTasks"
        :all-conversations-selected="fromAllConvos"
        :icon-name="cachedBreadcrumbData.iconName"
      />
      <i :class="arrowClasses" />
      <!-- Breadcrumb filter picker -->
      <breadcrumb-filter-picker
        v-if="$screen.isTabletOrDesktop($mq)"
        :default-filters="cachedBreadcrumbData.defaultFilters"
        :custom-filters="cachedBreadcrumbData.customFilters"
        :current-filter="cachedBreadcrumbData.currentFilter"
        :total-item-count="cachedBreadcrumbData.totalItemCount"
        @change-filter="(val) => $emit('change-filter', val)"
        @navigate="(item: unknown) => $emit('navigate', item)"
      />
      <i :class="arrowClasses" />
      <!-- Task and convo detail route.params passes ref # to prevent flickering -->
      <breadcrumb-item-picker
        :item-ref-number="itemRefNumber"
        :item-title="itemTitle"
        :show-item-title="showItemTitle"
        :items="breadcrumbItems"
        @navigate="(item) => $emit('navigate', item)"
      />
    </template>
    <!-- Coming from anywhere else (external, calendar, linked task, etc.)... No arrows/navigation -->
    <template v-else>
      <breadcrumb-listing-picker
        :title="title"
        :selected-id="selectedEntityId"
        :icon-name="iconName"
      />
      <i :class="arrowClasses" />
      <breadcrumb-item-picker
        :item-ref-number="itemRefNumber"
        :item-title="itemTitle"
        :show-item-title="showItemTitle"
      />
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'
import {
  IBreadcrumbDataFromListing,
  INavMenuItem,
} from '@/types/data-objects/global'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'

/* Data types */
import { EEntities } from '@/types'
import { IBreadcrumbItemMenuItem } from '@/types/data-objects/items'

/* API */

export default defineComponent({
  name: 'BreadcrumbForItemDetail',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  props: {
    entityType: {
      type: String as () => EEntities,
      required: true,
    },

    title: {
      type: String,
      default: '',
    },

    selectedEntityId: {
      type: Number,
      default: null,
    },

    itemRefNumber: {
      type: Number,
      default: null,
    },

    itemTitle: {
      type: String,
      default: '',
    },

    showItemTitle: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['change-filter', 'navigate'],

  data() {
    return {
      defaultFilters: [] as INavMenuItem[],
      customFilters: [] as INavMenuItem[],
      currentFilter: {} as INavMenuItem,
    }
  },

  computed: {
    arrowClasses(): string {
      if (this.$screen.isTabletOrDesktop(this.$mq)) {
        return 'ddi ddi-caret-right'
      }
      return 'hidden'
    },
    fromAll(): boolean | any {
      return BreadcrumbAssistant.fromAll(this.$route)
    },
    fromAllTasks(): boolean {
      return this.fromAll && this.entityType === EEntities.PROJECTS
    },
    fromAllConvos(): boolean {
      return this.fromAll && this.entityType === EEntities.MAILBOXES
    },
    fromEntityLanding(): boolean | any {
      return BreadcrumbAssistant.fromEntityLanding(this.$route)
    },
    cachedBreadcrumbData(): IBreadcrumbDataFromListing {
      return BreadcrumbAssistant.lastVisitedBreadcrumbDataFromListing(
        )
    },
    breadcrumbItems(): IBreadcrumbItemMenuItem[] {
      return BreadcrumbAssistant.lastVisitedBreadcrumbItemMenuItems()
    },
    iconName(): string {
      if (this.fromAll) {
        return TextAssistant.itemSingularName(this.entityType).toLowerCase()
      }
      return TextAssistant.entitySingularName(this.entityType).toLowerCase()
    },
    previousItem(): IBreadcrumbItemMenuItem {
      return BreadcrumbAssistant.getPreviousBreadcrumbItem(
        this.itemRefNumber
      )
    },
    nextItem(): IBreadcrumbItemMenuItem {
      return BreadcrumbAssistant.getNextBreadcrumbItem(
        this.itemRefNumber
      )
    },
  },
})
</script>

<style lang="scss">
.breadcrumb-for-item-detail {
  display: flex;
  align-items: center;
  @include single-line-ellipsis;

  .breadcrumb-listing-picker {
    flex: 1 0 auto;
  }

  > .ddi-caret-right {
    color: var(--color-text-primary);
    font-size: 8px;
    padding: 0 14px;
  }

  > .hidden {
    display: none;
  }

  @include media-breakpoint-down(sm) {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    padding-right: 0;

    > *:not(:first-child) {
      margin-top: 10px;
    }
  }
}
</style>
